<template>
  <div class="status-page">
    <div class="header">
      <h1>系统服务状态</h1>
      <div class="overall" :class="overallClass">
        <span class="dot" />
        {{ overallText }}
      </div>
      <div class="meta">
        上次检查：{{ checkedAt || '检查中...' }}
        <button @click="loadAll" :disabled="loading">{{ loading ? '检查中...' : '立即刷新' }}</button>
      </div>
    </div>

    <div class="monitor-grid">
      <div class="monitor-card alert-card">
        <div class="card-title">当前告警</div>
        <div v-if="alerts.length === 0" class="empty-text">暂无当前告警</div>
        <div v-for="alert in alerts" :key="alert.id" class="alert-row" :class="'level-' + alert.level">
          <div class="alert-main">
            <span class="alert-name">{{ alert.name }}</span>
            <span class="alert-category">{{ categoryText(alert.category) }}</span>
          </div>
          <div class="alert-detail">{{ alert.detail }}</div>
          <div class="alert-meta">失败 {{ alert.failCount }} 次 · {{ formatTime(alert.lastSeen) }}</div>
        </div>
      </div>

      <div class="monitor-card stats-card">
        <div class="card-title">最近 5 分钟请求统计</div>
        <div class="stats-summary">
          <div><strong>{{ requestStats.total || 0 }}</strong><span>总请求</span></div>
          <div><strong>{{ requestStats.failed || 0 }}</strong><span>失败</span></div>
          <div><strong>{{ percent(requestStats.failureRate) }}</strong><span>失败率</span></div>
          <div><strong>{{ requestStats.p95DurationMs == null ? '-' : requestStats.p95DurationMs + 'ms' }}</strong><span>P95</span></div>
        </div>
        <div v-if="requestStats.topFailures && requestStats.topFailures.length" class="mini-list">
          <div v-for="item in requestStats.topFailures" :key="item.key" class="mini-row">
            <span>{{ item.key }}</span><b>{{ item.count }}</b>
          </div>
        </div>
        <div v-else class="empty-text">暂无失败请求</div>
      </div>
    </div>

    <div class="section-title" style="margin-top:32px">核心接口探针</div>
    <div class="probe-list">
      <div v-for="probe in probes" :key="probe.id" class="probe-row">
        <div class="probe-head">
          <span class="service-name">{{ probe.name }}</span>
          <span class="badge" :class="probe.ok ? 'badge-operational' : 'badge-outage'">{{ probe.ok ? '正常' : '异常' }}</span>
          <span class="probe-category">{{ categoryText(probe.category) }}</span>
        </div>
        <div class="probe-detail">{{ probe.method }} {{ probe.path }} · {{ probe.detail }} · {{ probe.latency == null ? '-' : probe.latency + 'ms' }}</div>
        <div class="alert-meta">{{ formatTime(probe.checkedAt) }}</div>
      </div>
      <div v-if="probes.length === 0" class="empty-text probe-empty">暂无探针数据</div>
    </div>

    <!-- 24小时细粒度 -->
    <div class="section-title">过去 24 小时</div>
    <div class="services">
      <div v-for="svc in services" :key="svc.id" class="service-row">
        <div class="service-info">
          <span class="service-name">{{ svc.name }}</span>
          <span class="badge" :class="'badge-' + currentStatus(svc.id)">
            {{ statusText(currentStatus(svc.id)) }}
          </span>
        </div>
        <div class="timeline">
          <div
            v-for="(bucket, i) in h24"
            :key="i"
            class="tick"
            :class="bucket.services[svc.id] || 'unknown'"
            :title="formatTick(bucket.ts, bucket.services[svc.id])"
          />
        </div>
        <div class="timeline-labels">
          <span>24小时前</span>
          <span>现在</span>
        </div>
      </div>
    </div>

    <!-- 90天概览 -->
    <div class="section-title" style="margin-top:40px">过去 90 天</div>
    <div class="services">
      <div v-for="svc in services" :key="'d_' + svc.id" class="service-row">
        <div class="service-info">
          <span class="service-name">{{ svc.name }}</span>
          <span class="uptime">{{ uptime(svc.id) }} 正常运行</span>
        </div>
        <div class="timeline">
          <div
            v-for="(bucket, i) in d90"
            :key="i"
            class="tick tick-day"
            :class="bucket.services[svc.id] || 'unknown'"
            :title="formatTick(bucket.ts, bucket.services[svc.id])"
          />
        </div>
        <div class="timeline-labels">
          <span>90天前</span>
          <span>今天</span>
        </div>
      </div>
    </div>

    <div class="legend">
      <span class="tick operational" /> 正常
      <span class="tick degraded" /> 部分异常
      <span class="tick outage" /> 中断
      <span class="tick unknown" /> 无数据
    </div>

    <div class="footer">每 5 分钟自动检查，页面每 30 秒刷新</div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "Status",
  data () {
    return {
      overall: null,
      checkedAt: null,
      currentServices: [],
      services: [],
      h24: [],
      d90: [],
      alerts: [],
      requestStats: {},
      probes: [],
      loading: false,
      timer: null
    };
  },
  computed: {
    overallClass () {
      return { operational: "overall-ok", degraded: "overall-degraded", outage: "overall-fail" }[this.overall] || "overall-unknown";
    },
    overallText () {
      return { operational: "所有服务正常运行", degraded: "部分服务异常", outage: "服务中断" }[this.overall] || "检查中...";
    }
  },
  mounted () {
    this.loadAll();
    this.timer = setInterval(this.loadAll, 30000);
  },
  beforeDestroy () {
    clearInterval(this.timer);
  },
  methods: {
    async loadAll () {
      this.loading = true;
      try {
        const [curRes, histRes, monitorRes] = await Promise.all([
          axios.get("/status-api"),
          axios.get("/status-api/history"),
          axios.get("/status-api/monitor")
        ]);
        const cur = curRes.data || {};
        const hist = histRes.data || {};
        const monitor = monitorRes.data || {};

        this.overall = cur.overall;
        this.checkedAt = new Date(cur.checkedAt).toLocaleString("zh-CN");
        this.currentServices = cur.services;
        this.services = hist.services;
        this.h24 = hist.h24;
        this.d90 = hist.d90;
        this.alerts = monitor.alerts || [];
        this.requestStats = monitor.requestStats || {};
        this.probes = monitor.probes || [];
      } catch (e) {
        this.overall = "outage";
      } finally {
        this.loading = false;
      }
    },
    currentStatus (id) {
      const service = this.currentServices.find(item => item.id === id);

      if (!service) {
        return "unknown";
      }
      return service.ok ? "operational" : "outage";
    },
    statusText (s) {
      return { operational: "正常", degraded: "部分异常", outage: "异常", unknown: "未知" }[s] || "未知";
    },
    uptime (id) {
      const total = this.d90.filter(b => b.services[id] && b.services[id] !== "unknown").length;
      const ok = this.d90.filter(b => b.services[id] === "operational").length;

      if (!total) {
        return "暂无数据";
      }
      return (ok / total * 100).toFixed(2) + "%";
    },
    formatTick (ts, status) {
      const d = new Date(ts);
      const s = { operational: "正常", degraded: "部分异常", outage: "中断", unknown: "无数据" }[status] || "无数据";

      return `${d.toLocaleString("zh-CN")} - ${s}`;
    },
    percent (value) {
      if (value === null || value === undefined) {
        return "0.0%";
      }
      return `${(Number(value) * 100).toFixed(1)}%`;
    },
    categoryText (category) {
      return {
        service: "服务",
        request: "请求",
        probe: "接口",
        business: "业务"
      }[category] || category || "监控";
    },
    formatTime (value) {
      if (!value) {
        return "-";
      }
      return new Date(value).toLocaleString("zh-CN");
    }
  }
};
</script>

<style scoped>
.status-page {
  min-height: 100vh;
  width: 100%;
  background: #0f1117;
  color: #e2e8f0;
  font-family: 'Microsoft YaHei', sans-serif;
  padding: 48px 24px;
  box-sizing: border-box;
  text-align: left;
}
.status-page > * { max-width: 900px; margin-left: auto; margin-right: auto; }

.header { margin-bottom: 40px; }
h1 { font-size: 28px; font-weight: 600; margin-bottom: 16px; }

.overall {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 18px; font-weight: 500;
  padding: 8px 16px; border-radius: 8px; margin-bottom: 12px;
}
.overall-ok      { background: #14532d; color: #4ade80; }
.overall-degraded{ background: #713f12; color: #fbbf24; }
.overall-fail    { background: #450a0a; color: #f87171; }
.overall-unknown { background: #1e293b; color: #94a3b8; }

.dot { width: 10px; height: 10px; border-radius: 50%; background: currentColor; display: inline-block; }

.meta { font-size: 13px; color: #64748b; margin-top: 8px; display: flex; align-items: center; gap: 12px; }
button { background: #1e293b; color: #94a3b8; border: 1px solid #334155; border-radius: 6px; padding: 4px 12px; cursor: pointer; font-size: 12px; }
button:hover { background: #334155; }
button:disabled { opacity: 0.5; cursor: not-allowed; }

.section-title { font-size: 13px; color: #64748b; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 16px; max-width: 900px; margin-left: auto; margin-right: auto; }

.monitor-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; max-width: 900px; margin: 0 auto 32px; }
.monitor-card { background: #161b27; border: 1px solid #1e293b; border-radius: 10px; padding: 16px 20px; }
.card-title { font-size: 13px; color: #94a3b8; margin-bottom: 14px; font-weight: 600; }
.empty-text { color: #64748b; font-size: 13px; padding: 8px 0; }
.alert-row { border-left: 3px solid #fbbf24; padding: 10px 0 10px 12px; border-bottom: 1px solid #1e293b; }
.alert-row:last-child { border-bottom: none; }
.alert-row.level-critical { border-left-color: #f87171; }
.alert-main { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.alert-name { color: #e2e8f0; font-weight: 600; }
.alert-category, .probe-category { font-size: 11px; color: #94a3b8; background: #1e293b; border-radius: 12px; padding: 1px 7px; }
.alert-detail { color: #cbd5e1; font-size: 13px; line-height: 1.5; }
.alert-meta { color: #64748b; font-size: 12px; margin-top: 5px; }
.stats-summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
.stats-summary div { background: #0f1117; border-radius: 8px; padding: 10px; }
.stats-summary strong { display: block; color: #e2e8f0; font-size: 18px; margin-bottom: 4px; }
.stats-summary span { color: #64748b; font-size: 12px; }
.mini-list { margin-top: 12px; display: flex; flex-direction: column; gap: 8px; }
.mini-row { display: flex; justify-content: space-between; gap: 12px; color: #cbd5e1; font-size: 12px; }
.mini-row span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mini-row b { color: #f87171; }
.probe-list { display: flex; flex-direction: column; gap: 12px; max-width: 900px; margin: 0 auto 32px; }
.probe-row { background: #161b27; border: 1px solid #1e293b; border-radius: 10px; padding: 14px 18px; }
.probe-head { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
.probe-detail { color: #cbd5e1; font-size: 13px; line-height: 1.5; }
.probe-empty { max-width: 900px; margin: 0 auto; }

.services { display: flex; flex-direction: column; gap: 20px; max-width: 900px; margin: 0 auto; }

.service-row { background: #161b27; border: 1px solid #1e293b; border-radius: 10px; padding: 16px 20px; }

.service-info { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
.service-name { font-size: 14px; font-weight: 500; }
.uptime { font-size: 12px; color: #64748b; margin-left: auto; }

.badge { font-size: 11px; padding: 2px 8px; border-radius: 20px; font-weight: 500; }
.badge-operational { background: #14532d; color: #4ade80; }
.badge-degraded    { background: #713f12; color: #fbbf24; }
.badge-outage      { background: #450a0a; color: #f87171; }
.badge-unknown     { background: #1e293b; color: #94a3b8; }

.timeline { display: flex; gap: 2px; align-items: center; }
.tick {
  flex: 1; height: 32px; border-radius: 3px; cursor: pointer;
  transition: opacity 0.15s;
}
.tick:hover { opacity: 0.75; }
.tick-day { height: 28px; }
.tick.operational { background: #4ade80; }
.tick.degraded    { background: #fbbf24; }
.tick.outage      { background: #f87171; }
.tick.unknown     { background: #1e293b; }

.timeline-labels { display: flex; justify-content: space-between; font-size: 11px; color: #475569; margin-top: 4px; }

.legend { display: flex; align-items: center; gap: 16px; margin-top: 32px; font-size: 12px; color: #64748b; max-width: 900px; margin-left: auto; margin-right: auto; }
.legend .tick { flex: none; width: 14px; height: 14px; border-radius: 2px; }

.footer { margin-top: 24px; font-size: 12px; color: #334155; text-align: center; max-width: 900px; margin-left: auto; margin-right: auto; }

@media (max-width: 768px) {
  .monitor-grid { grid-template-columns: 1fr; }
  .stats-summary { grid-template-columns: repeat(2, 1fr); }
  .probe-head { flex-wrap: wrap; }
}
</style>
